import React, { useEffect, useState } from 'react';
import { Alert } from 'antd';
import { useIntl } from 'umi';
import { formatUTCToLocalDateTime } from '@/utils/timeFormat';
import { getDeployNotice } from '@/services/ant-design-pro/system';
import {
  getSocket,
  onDeployNotice,
  offDeployNotice,
  onDeployNoticeCleared,
  offDeployNoticeCleared,
  type DeployNotice,
} from '@/utils/socket';
import { useAuthStore } from '@/stores/authStore';

/**
 * Sticky top banner shown to admins when the backend is about to deploy.
 *
 * Driven by:
 *  - Socket `admin:deploy:notice` → show banner (re-sent by server on reconnect)
 *  - Socket `admin:deploy:notice:cleared` → hide banner
 *  - REST `GET /admin/system/deploy-notice` on mount + on (re)connect, so the
 *    state is correct after a refresh or after the socket dropped during deploy.
 *
 * Admin-only; renders nothing when there is no active notice.
 */
const DeployNoticeBanner: React.FC = () => {
  const intl = useIntl();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [notice, setNotice] = useState<DeployNotice | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotice(null);
      return;
    }

    let cancelled = false;
    let pollId: ReturnType<typeof setInterval> | undefined;

    const handleNotice = (payload: { notice: DeployNotice }) => {
      if (!cancelled) setNotice(payload?.notice ?? null);
    };
    const handleCleared = () => {
      if (!cancelled) setNotice(null);
    };
    // Pull the authoritative current state from REST (covers refresh + reconnect).
    const syncFromRest = async () => {
      try {
        const res = await getDeployNotice();
        if (cancelled) return;
        if (res?.data?.active && res.data.notice) {
          setNotice(res.data.notice);
        } else {
          setNotice(null);
        }
      } catch {
        // Ignore — a socket event or a later sync will correct the state.
      }
    };

    syncFromRest();

    // The socket is created by SocketInitializer, whose effect may run after
    // this one. Poll until the socket exists, then attach listeners once.
    const attach = (): boolean => {
      const s = getSocket();
      if (!s) return false;
      onDeployNotice(handleNotice);
      onDeployNoticeCleared(handleCleared);
      // Re-sync whenever the socket (re)connects — e.g. after the deploy restart.
      s.on('connect', syncFromRest);
      return true;
    };

    if (!attach()) {
      pollId = setInterval(() => {
        if (attach() && pollId) clearInterval(pollId);
      }, 1000);
    }

    return () => {
      cancelled = true;
      if (pollId) clearInterval(pollId);
      offDeployNotice(handleNotice);
      offDeployNoticeCleared(handleCleared);
      getSocket()?.off('connect', syncFromRest);
    };
  }, [isAuthenticated]);

  if (!notice) return null;

  const expectedEnd = notice.expiresAt
    ? `${intl.formatMessage({ id: 'component.deployNotice.expectedEnd' }) || 'Expected end'}: ${formatUTCToLocalDateTime(notice.expiresAt)}`
    : '';

  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 1001 }}>
      <Alert
        banner
        type="warning"
        showIcon
        message={
          <span role="alert">
            {notice.message}
            {expectedEnd && (
              <span style={{ marginLeft: 8, opacity: 0.75 }}>({expectedEnd})</span>
            )}
          </span>
        }
      />
    </div>
  );
};

export default DeployNoticeBanner;
